import React, { forwardRef, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Modal, {
  ModalProps,
  ModalRef,
} from '@/components/@IntranetLibrary/Modal';
import {
  useFindPassport,
  useSaveEmployeeSearchHistory,
} from '@/hooks/apiHooks';
import LinkIcon from '@/public/icons/link2.svg';
import { useTranslation } from 'react-i18next';
import ProfileCard, { ProfileCardProps } from '../index';
import { useSnackbar } from '@/hooks/global-tools';
import CustomToolTip from '@/components/@IntranetLibrary/ProfileCard/CustomToolTip';
import { copyProfileCardLink } from '@/utils/function';

type UserProfileModalProps = Pick<ModalProps, 'handleClose'> &
  Pick<ProfileCardProps, 'view'> & {
    container?: HTMLElement;
    saveSearchData?: boolean;
  };

const ProfileCardModal = forwardRef<ModalRef, UserProfileModalProps>(
  (props, ref) => {
    const { handleClose, view, container, saveSearchData = true } = props;
    const [passportId, setPassportId] = useState('');
    const { data, isLoading } = useFindPassport(passportId);
    const { t } = useTranslation();
    const toast = useSnackbar();

    useEffect(() => {
      if (saveSearchData && data) {
        useSaveEmployeeSearchHistory({
          name: data.detail.name,
          passportId: data.passportId,
          positionName: data.detail.positionName,
          organizationShortName: data.detail.organizationShortName,
          profileImageUrl: data.detail.profileImageUrl,
          divisionName: data.detail.divisionName,
          departmentName: data.detail.departmentName,
        });
      }
    }, [data, saveSearchData]);

    const handleClick = async () => {
      const resultCopy = await copyProfileCardLink(data?.passportId);
      if (resultCopy) {
        toast.show({
          severity: 'success',
          message: '프로필 카드 링크가 복사되었습니다.',
          duration: 3000,
        });
      }
    };

    return (
      <Modal
        container={container}
        ref={ref}
        title={t('프로필 카드')}
        size={'small'}
        additionalButton={
          <CustomToolTip title={'프로필 카드 링크 복사'}>
            <Button onClick={handleClick}>
              <LinkIcon width={'24px'} height={'24px'} />
            </Button>
          </CustomToolTip>
        }
        sx={{
          '.modal-inner': { width: 'auto' },
          '.modal-body': { padding: 0 },
        }}
        onClick={(event) => {
          // Approval 목록에서 해당 모달을 닫을 때 이벤트 버블링으로 ApprovalModal 열리는 버그 처리를 위함
          // https://fnf.atlassian.net/browse/PORTAL-6991
          event.stopPropagation();
        }}
        onOpen={setPassportId}
        handleClose={handleClose}
      >
        <ProfileCard
          isLoading={isLoading}
          data={data}
          view={view}
          sx={{ padding: '32px 24px 32px' }}
        />
      </Modal>
    );
  },
);
ProfileCardModal.displayName = 'ProfileCardModal';

export default ProfileCardModal;
